import { createTheme } from '@mui/material/styles';

/** Filled and bordered buttons: global brand control */
const BUTTON_FILLED = '#1A1A1A';
const BUTTON_BORDER = '#1A1A1A';

const baseTheme = createTheme({
  palette: {
    primary: {
      main: '#326EEA', // Primary brand color
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#757575',
      light: '#9e9e9e',
      dark: '#616161',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    body1: {
      fontSize: '0.875rem',
    },
    body2: {
      fontSize: '0.75rem',
    },
  },
});

const theme = createTheme(baseTheme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontFamily: baseTheme.typography.fontFamily,
        },
        body: {
          fontFamily: baseTheme.typography.fontFamily,
        },
        '#root': {
          fontFamily: baseTheme.typography.fontFamily,
        },
        '*': {
          fontFamily: 'inherit',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        // `&&` doubles the class in Emotion, beating MuiButton's palette-based defaults,
        // which otherwise can win at rest and lose only in :hover.
        root: ({ ownerState }) => {
          const style = {
            textTransform: 'none',
            borderRadius: 4,
            padding: '8px 16px',
          };
          if (ownerState.variant === 'contained') {
            style.boxShadow = 'none';
          }
          if (ownerState.disabled) {
            return style;
          }
          if (ownerState.variant === 'contained') {
            style['&&'] = {
              backgroundColor: BUTTON_FILLED,
              color: '#ffffff',
              boxShadow: 'none',
            };
            style['&&:hover'] = {
              backgroundColor: BUTTON_FILLED,
              color: '#ffffff',
              boxShadow: 'none',
            };
            style['&&:active'] = {
              backgroundColor: BUTTON_FILLED,
              color: '#ffffff',
              boxShadow: 'none',
            };
          } else if (ownerState.variant === 'outlined') {
            style['&&'] = {
              borderColor: BUTTON_BORDER,
              color: BUTTON_BORDER,
            };
            style['&&:hover'] = {
              borderColor: BUTTON_BORDER,
              color: BUTTON_BORDER,
              backgroundColor: 'transparent',
            };
            style['&&:active'] = {
              borderColor: BUTTON_BORDER,
              color: BUTTON_BORDER,
              backgroundColor: 'transparent',
            };
          } else if (ownerState.variant === 'text') {
            style['&:hover'] = { backgroundColor: 'transparent' };
          }
          return style;
        },
        contained: { boxShadow: 'none' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        },
      },
    },
  },
});

export default theme;
